import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { shellCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { SkipLink, TopBar } from '@/ui/Console/Console'
import { Drawer } from '@/ui/Drawer/Drawer'
import { CommandPalette } from '../components/console/CommandPalette'
import { Rail } from '../components/console/Rail'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { useWebSocket } from '../hooks/useWebSocket'
import { useAuthStore } from '../store/auth'
import styles from './AppPage.module.css'

export function AppPage() {
  const { status, degraded } = useWebSocket()
  const username = useAuthStore((s) => s.username)
  const { pathname } = useLocation()

  const [railOpen, setRailOpen] = useState(false)
  const openRail = useCallback(() => setRailOpen(true), [])
  // The console band carries its own Menu (useBandMenu); the top bar shows
  // whenever no band Menu is mounted, including when the page has crashed.
  const [bandMenus, setBandMenus] = useState(0)
  const registerBandMenu = useCallback(() => {
    setBandMenus((n) => n + 1)
    return () => setBandMenus((n) => n - 1)
  }, [])
  const frame = useMemo(
    () => ({ openRail, registerBandMenu }),
    [openRail, registerBandMenu],
  )
  const mainRef = useRef<HTMLElement>(null)
  const lastPath = useRef(pathname)
  useEffect(() => {
    // accessibility: a route change moves focus to the new main heading (else main)
    if (lastPath.current === pathname) return
    lastPath.current = pathname
    const main = mainRef.current
    const heading = main?.querySelector('h1')
    if (heading && !heading.hasAttribute('tabindex'))
      heading.setAttribute('tabindex', '-1')
    ;(heading ?? main)?.focus()
  }, [pathname])

  const rail = (placement: 'permanent' | 'drawer') => (
    <Rail currentUser={username} placement={placement} />
  )

  return (
    <div className={styles.frame}>
      <SkipLink>{shellCopy.skipToMain}</SkipLink>
      {rail('permanent')}
      <div className={styles.column}>
        {bandMenus === 0 && (
          <TopBar>
            <Button variant="quiet" onClick={openRail}>
              {shellCopy.menu}
            </Button>
          </TopBar>
        )}
        <main
          id="main"
          ref={mainRef}
          tabIndex={-1}
          data-surface="page"
          className={styles.main}
        >
          {status === 'reconnecting' && (
            <div className={styles.notice}>
              <Alert tone="warning">{shellCopy.connection.reconnecting}</Alert>
            </div>
          )}
          {degraded && status === 'connected' && (
            <div className={styles.notice}>
              <Alert tone="warning">{shellCopy.connection.degraded}</Alert>
            </div>
          )}
          <ErrorBoundary>
            <Outlet context={frame} />
          </ErrorBoundary>
        </main>
      </div>
      <Drawer
        open={railOpen}
        onClose={() => setRailOpen(false)}
        title={shellCopy.menu}
      >
        {rail('drawer')}
      </Drawer>
      <CommandPalette />
    </div>
  )
}
